<template>
  <div>
    <CCard>
      <CCardHeader>
        <div class="preika-page-heading-copy">
          <h2>部署一覧</h2>
          <p>部署情報と組織構成を確認・管理します。</p>
        </div>
        <span v-if="canManage" class="card-header-actions">
          <router-link :to="{ name: registerTop }">
            <button type="button" class="btn btn-primary">新規登録</button>
          </router-link>
        </span>
      </CCardHeader>
      <CCardBody>
        <div class="preika-feature-actions">
          <button
            type="button"
            class="preika-feature-action"
            aria-haspopup="dialog"
            :aria-expanded="showOrgModal ? 'true' : 'false'"
            @click="openOrgChartModal"
          >
            <span class="preika-feature-action__mark" aria-hidden="true">組</span>
            <span class="preika-feature-action__copy">
              <strong>組織図を開く</strong>
              <small>親部署・子部署の階層を図で確認します</small>
            </span>
            <span class="preika-feature-action__arrow" aria-hidden="true">→</span>
          </button>
        </div>

        <CRow v-if="errors.length">
          <ul>
            <li class="preika-form-message" v-for="error in errors" :key="error">
              {{ error }}
            </li>
          </ul>
        </CRow>

        <ListSearchBox
          :value="keyword"
          label="部署名・コード・上位部署・部署長で絞り込む"
          placeholder="例: 営業部、D-002、山田"
          :count="keyword ? totalCount : null"
          @search="applyKeyword"
        />

        <!-- 0件は失敗ではないので、赤字の欄（errors）ではなく案内の字で、表のすぐ上に出す。 -->
        <p v-if="emptyNote" class="text-muted">{{ emptyNote }}</p>
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>部署名</th>
                <th>部署コード</th>
                <th>親部署</th>
                <th>部署長</th>
                <th v-if="canManage" style="width: 120px;"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in items" :key="item.id">
                <td>
                  <router-link v-if="canManage" :to="{ name: info, params: { id: item.id } }">
                    {{ item.name }}
                  </router-link>
                  <span v-else>
                    {{ item.name }}
                  </span>
                </td>
                <td>{{ item.code }}</td>
                <td>{{ item.parent_name }}</td>
                <td>{{ item.head_user_name }}</td>
                <td v-if="canManage">
                  <button
                    v-on:click="openModal(item)"
                    class="btn btn-danger"
                  >
                    無効
                  </button>
                  <DeleteModal
                    :val="postItem"
                    :loading="loading"
                    v-show="showModal"
                    @delete="deleteDepartment"
                    @close="closeModal"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex justify-content-center">
          <VPagination
            v-model="currentPage"
            :click-handler="changePage"
            :page-count="totalPages"
            :force-page="currentPage"
            :force-ellipses="true"
            :container-class="'pagination'"
            :page-class="'page-item'"
            :prev-text="'<'"
            :next-text="'>'"
            :prev-link-class="'page-link'"
            :next-link-class="'page-link'"
            :page-link-class="'page-link'"
            :class-prefix="'pagination'"
          />
        </div>
        <OrganizationChartModal
          :items="orgItems"
          :loading="orgLoading"
          v-show="showOrgModal"
          @close="closeOrgChartModal"
        />
        <!--
          **戻せない人には出さない。**この節が叩く /departments/deleted は
          USER_MANAGE が要る。部署の参照権（DEPARTMENT_VIEW）だけの人が
          この画面を開くと、節が開いた瞬間に必ず取得に失敗して赤い帯が出る。
          部品側に権限の判定は無いので、出す側で止める。
        -->
        <DeletedRowsPanel
          v-if="canManage"
          label="削除した部署"
          list-url="/departments/deleted"
          restore-url="/departments/{id}/restore"
          :columns="[
            { key: 'name', label: '部署名' },
            { key: 'code', label: '部署コード' },
            { key: 'memberCount', label: '在籍している人', suffix: '人' },
            { key: 'updatedUser', label: '最後に触った人' }
          ]"
          empty-message="削除した部署はありません。"
          note="戻すと、その部署に所属したままの社員の表示も元へ戻ります。消したあとに別の部署へ移した人は、そちらのままです。"
          @restored="reloadAfterRestore"
        />
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import DeletedRowsPanel from '@/components/DeletedRowsPanel'
import ConstList from './../ConstList'
import { requestFailureMessage } from '@/utils/requestFailure'
import DeleteModal from './DeleteModal'
import OrganizationChartModal from './OrganizationChartModal'
import axios from 'axios'
import VPagination from 'vuejs-paginate'
import ListSearchBox from '@/common/ListSearchBox'
import { getAccessContext } from '../services/accessContext'
import { safeErrorDigest } from '../utils/safeResponseMessage'

export default {
  name: 'DepartmentList',
  components: {
    DeletedRowsPanel,
    DeleteModal,
    OrganizationChartModal,
    VPagination,
    ListSearchBox
  },
  data() {
    return {
      showModal: false,
      showOrgModal: false,
      // 削除の小窓（DeleteModal、val: Object）へ渡す行。小窓は v-show で常に描かれ、val.name を
      // 直接読むので null にはしない（TypeError になる）。空の {} なら今までどおり空欄で描かれる。
      postItem: {},
      items: [],
      orgItems: [],
      errors: [],
      // 0件のときの案内（失敗ではないので errors には入れない）
      emptyNote: '',
      registerTop: '',
      info: '',
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      /* いま効いている絞り込みの言葉。空なら絞り込んでいない。 */
      keyword: '',
      totalCount: 0,
      submit_flg: false,
      loading: false,
      orgLoading: false,
      accessContext: null
    }
  },
  computed: {
    canManage: function () {
      const permissions = this.accessContext && Array.isArray(this.accessContext.permissions)
        ? this.accessContext.permissions
        : []
      const features = this.accessContext && Array.isArray(this.accessContext.features)
        ? this.accessContext.features
        : []
      return features.includes('USER_MANAGEMENT') && permissions.includes('USER_MANAGE')
    }
  },
  created() {
    this.registerTop = ConstList.DEPARTMENT_REGISTER_TOP
    this.info = ConstList.DEPARTMENT_INFO_TOP
  },
  mounted: function () {
    this.loadAccessContext()
    this.callList()
    this.fetchTotalPages()
  },
  methods: {
    loadAccessContext: function () {
      return getAccessContext()
        .then((context) => {
          this.accessContext = context
        })
        .catch((error) => {
          console.error('部署画面の権限情報を取得できませんでした:', safeErrorDigest(error))
          this.accessContext = null
        })
    },
    callList: function () {
      axios
        .get(process.env.VUE_APP_API_ORIGIN + '/departments', {
          params: {
            currentPage: this.currentPage,
            pageSize: this.pageSize,
            keyword: this.keyword || undefined
          }
        })
        .then((response) => {
          this.items = response.data
          this.errors = []
          this.emptyNote = this.items.length === 0 ? ConstList.EMPTY : ''
        })
        .catch((error) => {
          axios
            .post(`${process.env.VUE_APP_API_ORIGIN}/error/frontError`, { error: error.message || String(error) }, { headers: { 'Content-Type': 'application/json' } })
            .then(() => {
              alert(requestFailureMessage(error))
            })
            .catch(() => {
              alert(requestFailureMessage(error, ConstList.SYSTEM_COMMUNICATION_ERROR))
            })
        })
    },
    fetchTotalPages: function () {
      axios
        .get(process.env.VUE_APP_API_ORIGIN + '/departments/count', {
          params: { keyword: this.keyword || undefined }
        })
        .then((response) => {
          this.totalCount = Number(response.data) || 0
          this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize))
        })
        .catch((error) => {
          axios
            .post(`${process.env.VUE_APP_API_ORIGIN}/error/frontError`, { error: error.message || String(error) }, { headers: { 'Content-Type': 'application/json' } })
            .then(() => {
              alert(requestFailureMessage(error))
            })
            .catch(() => {
              alert(requestFailureMessage(error, ConstList.SYSTEM_COMMUNICATION_ERROR))
            })
        })
    },
    // 削除した部署を戻したあと、一覧だけを読み直す（画面ごと読み直すと、戻せたという文も節も消える）。
    reloadAfterRestore: function () {
      this.callList()
      this.fetchTotalPages()
    },
    changePage: function (page) {
      this.currentPage = page
      this.callList()
    },

    /**
     * 絞り込みを受ける。
     *
     * **ページを1へ戻す。**5ページ目のまま絞り込むと、結果が1ページ
     * しか無いときに空の表になる。件数も先に数え直す。
     */
    applyKeyword (keyword) {
      this.keyword = keyword
      this.currentPage = 1
      Promise.resolve(this.fetchTotalPages()).then(() => this.callList())
    },
    openModal: function (item) {
      if (this.submit_flg || !this.canManage) return
      this.postItem = item
      this.showModal = true
    },
    closeModal: function () {
      this.showModal = false
    },
    deleteDepartment: function () {
      if (!this.canManage) return
      this.loading = true
      const params = new URLSearchParams()
      params.append('id', this.postItem.id)

      axios
        .post(process.env.VUE_APP_API_ORIGIN + '/departments/delete', params)
        .then(() => {
          alert(ConstList.DELETE_MESSAGE)
          this.showModal = false
          this.callList()
          this.fetchTotalPages()
        })
        .catch((error) => {
          axios
            .post(`${process.env.VUE_APP_API_ORIGIN}/error/frontError`, { error: error.message || String(error) }, { headers: { 'Content-Type': 'application/json' } })
            .then(() => {
              alert(requestFailureMessage(error))
            })
            .catch(() => {
              alert(requestFailureMessage(error, ConstList.SYSTEM_COMMUNICATION_ERROR))
            })
        })
        .finally(() => {
          this.loading = false
        })
    },
    numberOrZero: function (v) {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    },
    openOrgChartModal: function () {
      this.showOrgModal = true
      this.fetchOrgItems()
    },
    closeOrgChartModal: function () {
      this.showOrgModal = false
    },
    fetchOrgItems: function () {
      this.orgLoading = true

      axios
        .get(process.env.VUE_APP_API_ORIGIN + '/departments/count')
        .then((countResponse) => {
          const totalCount = Number(countResponse.data)
          const allCount = Number.isFinite(totalCount) && totalCount > 0 ? totalCount : 1

          return axios.get(process.env.VUE_APP_API_ORIGIN + '/departments', {
            params: {
              currentPage: 1,
              pageSize: allCount
            }
          })
        })
        .then((response) => {
          this.orgItems = Array.isArray(response.data) ? response.data : []
        })
        .catch((error) => {
          axios
            .post(`${process.env.VUE_APP_API_ORIGIN}/error/frontError`, { error: error.message || String(error) }, { headers: { 'Content-Type': 'application/json' } })
            .then(() => {
              alert(requestFailureMessage(error))
            })
            .catch(() => {
              alert(requestFailureMessage(error, ConstList.SYSTEM_COMMUNICATION_ERROR))
            })
        })
        .finally(() => {
          this.orgLoading = false
        })
    }
  }
}
</script>

<style scoped>
.table td.position-right,
.table th.position-right {
  text-align: right !important;
  padding-right: 10px;
}

td {
  vertical-align: middle;
  white-space: nowrap;
}

.preika-page-heading-copy {
  position: relative;
  z-index: 1;
  min-width: 0;
}

.preika-page-heading-copy p {
  margin: 7px 0 0;
  color: rgba(255, 255, 255, .8);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.55;
}
</style>
